import { accountSvc as service, projectSvc, lookupSvc, userSettingsSvc } from "services/serviceStorage";
import { BaseApiResponse } from "common/types";
import { wrapRequest } from "common/utils";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { Account, UserLinks, EditModalLookups, AccountEditModalSource } from "./types";
import { displayErrors, mapMultiselectLookup, mapLookup } from "common/helpers";
import some from 'lodash/some'


export const fetchAccounts = createAsyncThunk<{ data: Account[], total: number }, void>(
    'accountMgmt/fetchAccounts',
    async () => {
        const { data } = await wrapRequest<Account[]>({
            handler: () => service.list(),
            showNotifications: true
        });

        const mappedData = {
            data: data?.map(i => ({ ...i, key: i.userId, userRole: i.userRoles[0] })) || [],
            total: data?.length || 0,
        }

        return mappedData;
    }
);

export const fetchAccountLinks = createAsyncThunk<UserLinks[], string[]>(
    'accountMgmt/fetchAccountLinks',
    async (userIds) => {
        const { data } = await wrapRequest<BaseApiResponse<UserLinks[]>>({
            handler: () => projectSvc.getUsersLinks(userIds),
            showNotifications: true
        });

        return data?.result || [];
    }
);

export const fetchEditModalLookups = createAsyncThunk<EditModalLookups, void>(
    "accountMgmt/fetchEditModalLookups",
    async (_, { rejectWithValue }) => {
        try {
            const [projectCodesRes, userRolesRes, projectsRes] = await Promise.all([
                lookupSvc.getProjectCodes(),
                lookupSvc.getUserRoles(),
                lookupSvc.getProjects(),
            ]);

            return {
                projectCodes: projectCodesRes.data.result.map(mapMultiselectLookup),
                userRoles: userRolesRes.data.result.map(mapLookup),
                projects: projectsRes.data.result.map(mapMultiselectLookup),
            };
        } catch (error) {
            displayErrors(["Internal server error. Please, contact system administrator."]);
            return rejectWithValue(error)
        }
    }
);

export const createAccount = createAsyncThunk<string, AccountEditModalSource>(
    'accountMgmt/createAccount',
    async (account) => {
        const { data } = await wrapRequest<string>({
            handler: () => service.create(account),
            showNotifications: true
        });

        return data!;
    }
);

export const updateAccount = createAsyncThunk<string, AccountEditModalSource>(
    'accountMgmt/updateAccount',
    async (account) => {
        const { data } = await wrapRequest<string>({
            handler: () => service.update(account),
            showNotifications: true
        });

        return data!;
    }
);

export const modifyUserLinks = createAsyncThunk<void, { userId: string; projectIds: number[]; projectCodes: string[]; }>(
    'accountMgmt/modifyUserLinks',
    async (request, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => projectSvc.modifyUserLinks(request),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        };
    }
);

export const updateUserSettings = createAsyncThunk<void, { userId: string; settings: { maxSegmentsPerRun: number; removeCubeLocksTimeUtc: string } }>(
    'accountMgmt/updateUserSettings',
    async ({ userId, settings }, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => userSettingsSvc.updateSettings(userId, settings),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        };
    }
);

export const deleteAccount = createAsyncThunk<void, string>(
    'accountMgmt/deleteAccount',
    async (userId, { rejectWithValue }) => {
        const err = await wrapRequest<BaseApiResponse<void>>({
            handler: () => service.delete(userId),
            showNotifications: true
        });

        if (err.errors?.length || err.warnings?.length) {
            return rejectWithValue(err);
        };
    }
);

export const getAccount = createAsyncThunk<Account, string>(
    'accountMgmt/getAccount',
    async (userId) => {
        const { data } = await wrapRequest<Account>({
            handler: () => service.get(userId),
            showNotifications: true
        });

        const mappedData = {
            ...data,
            userRole: data?.userRoles[0],
            isSuperAdmin: some(data?.userRoles, data => data === "superadmin")
        };

        return mappedData as Account;
    }
);

export const fetchUserSettings = createAsyncThunk<{ maxSegmentsPerRun: number | null, removeCubeLocksTimeUtc: string | null }, string>(
    'accountMgmt/fetchUserSettings',
    async (userId) => {
        const { data } = await wrapRequest<BaseApiResponse<{ maxSegmentsPerRun: number | null, removeCubeLocksTimeUtc: string | null }>>({
            handler: () => userSettingsSvc.getSettings(userId),
            showNotifications: true
        });

        return data!.result;
    }
);